import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Menu, MenuItem } from "@mui/material";
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";

import { useMemo } from "react";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { MuiIconButton } from "../../../../../../components";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  STATUS_BADGE_TYPE,
  StatusBadgeType,
} from "../../../../../../components/statusBadge/StatusBadge";
import { MODULE_PERMISSIONS } from "../../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../../helpers/constants/componentIds";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getDocumentStatusFromExpirationDate,
  getValueOrBlank,
  isDocumentInherited,
  isStatusActive,
  isStatusAutoRenewed,
  isStatusExpired,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { ClientDocument } from "../../../../modelsAndSchemas/Models";
import { documentSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { OptionMenuWrapper } from "./OptionMenu.style";

export interface OptionMenuProps {
  clientData: ClientDocument;
  onClickOptionEdit: (row: ClientDocument) => void;
  onClickOptionDelete: (row: ClientDocument) => void;
  onClickCancelContract: (row: ClientDocument) => void;
  onClickOptionMarkAsActive: (row: ClientDocument) => void;
  onClickOptionMarkAsExpired: (row: ClientDocument) => void;
  onClickOptionMarkAsArchive: (row: ClientDocument) => void;
  onClickOptionUnMap?: (row: ClientDocument) => void;
}

export const OptionMenu: React.FC<OptionMenuProps> = ({
  clientData,
  onClickOptionEdit,
  onClickOptionDelete,
  onClickCancelContract,
  onClickOptionMarkAsActive,
  onClickOptionMarkAsExpired,
  onClickOptionMarkAsArchive,
  onClickOptionUnMap,
}) => {
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const popupState = usePopupState({
    popupId: `${clientData.documentId}`,
    variant: "popover",
  });
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const expirationDateFormatted = useMemo(
    () => convertToUserFormat(clientData.expirationDate, userCountryDateFormat),
    [clientData.expirationDate, userCountryDateFormat],
  );
  const documentStatus = getDocumentStatusFromExpirationDate(
    expirationDateFormatted,
  ) as StatusBadgeType;

  return (
    <OptionMenuWrapper>
      <MuiIconButton
        className="option-menu-icon"
        {...bindTrigger(popupState)}
        id={`${COMPONENT_IDS.COMMON_OPTION_MENU}-${clientData.documentId}`}
        data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU}-${clientData.documentId}`}
        aria-label={commonARIALabel.moreOptionsForThisTableRow}
      >
        <MoreVertIcon />
      </MuiIconButton>

      <Menu {...bindMenu(popupState)}>
        {!clientData.isCancelled && (
          <PermissionWrapper
            permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
          >
            <MenuItem
              data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU_EDIT}-${clientData.documentId}`}
              onClick={() => {
                popupState.close();
                onClickOptionEdit(clientData);
              }}
            >
              {commonLabels.edit}
            </MenuItem>
          </PermissionWrapper>
        )}

        {!(
          [
            STATUS_BADGE_TYPE.cancelled,
            STATUS_BADGE_TYPE.autoRenewed,
            STATUS_BADGE_TYPE.expired,
          ] as string[]
        ).includes(clientData.status) && (
          <PermissionWrapper
            permission={MODULE_PERMISSIONS.DELETE_CLIENT_DOCUMENTS}
          >
            <MenuItem
              data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU_DELETE}-${clientData.documentId}`}
              onClick={() => {
                popupState.close();
                onClickOptionDelete(clientData);
              }}
            >
              {commonLabels.delete}
            </MenuItem>
          </PermissionWrapper>
        )}
        {(clientData.status === STATUS_BADGE_TYPE.active ||
          clientData.status === STATUS_BADGE_TYPE.autoRenewed) &&
          !clientData.isCancelled && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.OPTION_MENU_CANCEL_CONTRACT}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickCancelContract(clientData);
                }}
              >
                {fetchLabel(
                  documentSiteCoreKeys.documentsTitle,
                  documentSiteCoreKeys.cancelContract,
                )}
              </MenuItem>
            </PermissionWrapper>
          )}
        {!isDocumentInherited(getValueOrBlank(clientData.documentOrigin)) &&
          (clientData.status === STATUS_BADGE_TYPE.cancelled ||
            clientData.status === STATUS_BADGE_TYPE.expired) && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.OPTION_MENU_MARK_AS_ARCHIVE}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickOptionMarkAsArchive(clientData);
                }}
              >
                {commonLabels.archive}
              </MenuItem>
            </PermissionWrapper>
          )}
        {clientData.isCancelled &&
          documentStatus === STATUS_BADGE_TYPE.active && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.OPTION_MENU_MARK_AS_ACTIVE}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickOptionMarkAsActive(clientData);
                }}
              >
                {commonLabels.undoCancel}
              </MenuItem>
            </PermissionWrapper>
          )}

        {clientData.isCancelled &&
          documentStatus === STATUS_BADGE_TYPE.expired && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.OPTION_MENU_MARK_AS_EXPIRED}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickOptionMarkAsExpired(clientData);
                }}
              >
                {commonLabels.undoCancel}
              </MenuItem>
            </PermissionWrapper>
          )}
        {isDocumentInherited(getValueOrBlank(clientData.documentOrigin)) &&
          (isStatusActive(clientData.status as StatusBadgeType) ||
            isStatusAutoRenewed(clientData.status as StatusBadgeType)) && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU_UNMAP}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickOptionUnMap?.(clientData);
                }}
              >
                {commonLabels.unmap}
              </MenuItem>
            </PermissionWrapper>
          )}
        {isDocumentInherited(getValueOrBlank(clientData.documentOrigin)) &&
          isStatusExpired(documentStatus) && (
            <>
              <PermissionWrapper
                permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
              >
                <MenuItem
                  data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU_UNMAP}-${clientData.documentId}`}
                  onClick={() => {
                    popupState.close();
                    onClickOptionUnMap?.(clientData);
                  }}
                >
                  {commonLabels.unmap}
                </MenuItem>
              </PermissionWrapper>
              <PermissionWrapper
                permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
              >
                <MenuItem
                  data-testid={`${COMPONENT_IDS.OPTION_MENU_MARK_AS_EXPIRED}-${clientData.documentId}`}
                  onClick={() => {
                    popupState.close();
                    onClickOptionMarkAsArchive(clientData);
                  }}
                >
                  {commonLabels.archive}
                </MenuItem>
              </PermissionWrapper>
            </>
          )}
      </Menu>
    </OptionMenuWrapper>
  );
};
